<template>
    <or-modal
        ref="featuresModal"
        dismiss-on="close-button"
        class="feature-modal"
        :title="modalTitle"
        @close="closeModel"
    >
        <div
            v-if="areOptionsLoading"
            class="feature-modal--loader"
        >
          {{ $t('common.modals.feature_modal.loadingFeatures') }}
        </div>
        <div
            v-else-if="featureOptions.length === 0"
            class="features-list--empty-body"
        >
            <div class="no-features-text">
              {{ $t('common.modals.feature_modal.youHaveNo') }}
            </div>
            <div>
                <or-button
                    type="secondary"
                    color="primary"
                    size="small"
                    class="flat create-btn no-features-btn"
                    disabledRipple
                    @click="openFeatureSlider"
                >
                  {{ $t('common.modals.feature_modal.createFeature') }}
                </or-button>
                <span class="no-features-or">
                    {{ $t('generic.or') }}
                </span>
                <or-button
                    type="secondary"
                    color="primary"
                    size="small"
                    class="flat create-btn no-features-btn"
                    disabledRipple
                    @click="openEntitySlider"
                >
                  {{ $t('common.modals.feature_modal.createEntity') }}
                </or-button>
            </div>
        </div>
        <div
            v-else
            class="features-list--body"
        >
            <div class="feature-modal--header">
                <span>
                  {{ $t('common.modals.feature_modal.chooseFeaturesAnd') }}
                </span>
                <or-button
                    class="feature-modal--create-new--btn"
                    type="secondary"
                    color="primary"
                    @click="openFeatureSlider"
                >
                  {{ $t('common.modals.feature_modal.newFeature') }}
                </or-button>
                <span>
                    or
                </span>
                <or-button
                    class="feature-modal--create-new--btn create-entity-btn"
                    type="secondary"
                    color="primary"
                    @click="openEntitySlider"
                >
                  {{ $t('common.modals.feature_modal.newEntity') }}
                </or-button>
            </div>
            <or-search-box
                v-model="searchText"
                class="feature-search"
                :placeholder-text="$t('generic.search')"
            />
            <div class="features-list">
                <div
                    v-if="filteredFeatures.length === 0 && filteredModels.length === 0"
                >
                    <div>{{ $t('common.modals.feature_modal.noResults') }}</div>
                    <div v-if="searchText">
                        <div>{{ $t('common.modals.feature_modal.weCouldntFind', { '1': searchText }) }}</div>
                        <div>{{ $t('common.modals.feature_modal.pleaseUpdateYour') }}</div>
                    </div>
                </div>
                <or-collapsible
                    v-if="filteredFeatures.length !== 0"
                    :title="$t('common.modals.feature_modal.features')"
                    borderless
                    open
                >
                    <div
                        v-for="feature in filteredFeatures"
                        :key="feature.id"
                        class="feature--item"
                    >
                        <or-checkbox
                            class="feature-option--checkbox"
                            :value="isOptionSelected(feature, 'feature')"
                            @change="toggleOption(feature, 'feature')"
                            color="accent"
                        >
                            {{feature.name}}
                        </or-checkbox>

                        <div class="feature-options--label">
                        <span v-if="feature.enabledForAllModels">
                          {{ $t('common.modals.feature_modal.global') }}
                        </span>
                            <span v-if="feature.mode">
                              {{ $t('common.modals.feature_modal.interchangeable') }}
                            </span>
                        </div>
                    </div>
                </or-collapsible>
                <or-collapsible
                    v-if="filteredModels.length !== 0"
                    :title="$t('common.modals.feature_modal.entities')"
                    borderless
                    open
                >
                    <div
                        v-for="model in filteredModels"
                        :key="model.id"
                        class="feature--item"
                    >
                        <or-checkbox
                            class="feature-option--checkbox"
                            :value="isOptionSelected(model, 'model')"
                            @change="toggleOption(model, 'model')"
                            color="accent"
                        >
                            {{model.name}}
                        </or-checkbox>
                        <entity-type-label :entityType="model.type" />
                    </div>
                </or-collapsible>
            </div>
        </div>
      <div slot="footer">
        <div
          v-if="filteredFeatures.length !== 0 || filteredModels.length !== 0"
          slot="footer"
          class="features--actions"
        >
          <or-button
            type='secondary'
            @click="closeModel"
            :loading="$props.updating"
          >
            {{ $t('generic.cancel') }}
          </or-button>
          <or-button
            color="primary"
            @click="saveFeatures"
            :loading="$props.updating"
          >
            {{ $props.newFeatures ? $t('generic.add') : $t('generic.save')}}
          </or-button>
        </div>
      </div>
    </or-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import KnowledgeBus from '@/KnowledgeBus'
import EntityTypeLabel from '@/components/model/common/EntityTypeLabel'
import { ENTITY_TYPE } from '@/components/model/nlu-model/sidebar/entities/entity-types'

export default {
  name: 'feature_modal',

  components: {
    EntityTypeLabel
  },

  props: {
    opened: Boolean,
    selectedFeatures: Array,
    newFeatures: Boolean
  },

  mounted () {
    this.fetchFeatures()
  },

  data () {
    return {
      searchText: '',
      localSelectedFeatures: _.cloneDeep(this.$props.selectedFeatures) || []
    }
  },

  computed: {
    ...mapState('knowledgebase/features', ['features', 'status']),
    ...mapState('knowledgebase/entities', ['allEntities']),

    // In fact not only features but also Entities
    featureOptions () {
      return [...this.features, ...this.allEntities.value]
    },

    areOptionsLoading () {
      return this.status.fetching || this.allEntities.loading
    },

    modalTitle () {
      return 'Add Features and Entities to Response'
    },

    filteredFeatures () {
      const features = this.features.filter(feature => feature.name.includes(this.searchText))
      features.sort((a, b) => a.name < b.name ? -1 : 1)
      return features
    },

    filteredModels () {
      const entities = this.allEntities.value
        .filter(model => model.type !== ENTITY_TYPE.patternAny)
        .filter(model => model.name.includes(this.searchText))
      entities.sort((a, b) => a.name < b.name ? -1 : 1)
      return entities
    }
  },

  methods: {
    ...mapActions('knowledgebase/features', ['fetchFeatures']),

    isOptionSelected ({ name }, type) {
      return this.localSelectedFeatures.some(
        ({ featureName, modelName }) => type === 'feature' ? featureName === name : modelName === name
      )
    },

    toggleOption ({ name }, type) {
      const isSelected = this.localSelectedFeatures.some(
        ({ featureName, modelName }) => type === 'feature' ? featureName === name : modelName === name
      )
      if (!isSelected) {
        this.localSelectedFeatures.push({
          [type === 'feature' ? 'featureName' : 'modelName']: name,
          isRequired: false
        })
      } else {
        this.localSelectedFeatures = this.localSelectedFeatures.filter(
          ({ featureName, modelName }) => type === 'feature' ? featureName !== name : modelName !== name
        )
      }
    },

    openFeatureSlider () {
      this.closeModel()
      KnowledgeBus.$emit('sidebar::open', 5)
    },

    openEntitySlider () {
      this.closeModel()
      KnowledgeBus.$emit('sidebar::open', 4)
    },

    closeModel () {
      this.localSelectedFeatures = _.cloneDeep(this.$props.selectedFeatures) || []
      this.$emit('close')
    },

    saveFeatures () {
      this.$emit('update-features', this.localSelectedFeatures)
    }
  },

  watch: {
    selectedFeatures: {
      handler (value) {
        this.localSelectedFeatures = _.cloneDeep(value)
      },
      deep: true
    },

    opened (isOpened) {
      if (isOpened) {
        this.$refs.featuresModal.open()
      } else {
        this.$refs.featuresModal.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
    .feature-modal {
        ::v-deep .ui-modal__footer {
            border-top: 0;
        }

        .create-entity-btn {
            margin-left: -4px !important;
        }

        .no-features-btn {
            padding: 0;
            font-size: 14px;
            font-weight: 600;
            text-align: center;
        }

        .features-modal--loader {
            height: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-style: italic;
        }

        .feature-modal--header {
            background: rgba(64, 159, 236, 0.1);
            padding: $sp-3;
            border-radius: 0;
            font-size: 14px;
            font-weight: normal;
            .feature-modal--create-new--btn {
                border: 0;
                padding: 0;
                margin-left: 0;
                font-size: 14px;
                font-weight: normal;
            }
        }

        .features-list--body {
            // (Top + bottom paddings = modal margin) - (title height) - (header height) - (footer height)
            max-height: calc(100vh - 64px - 24px - 58px - 70px);
        }

        .features-list--empty-body {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            margin: 16px 0;
            .no-features-text, .no-features-or {
                color: rgb(30, 35, 43);
                font-size: 14px;
                font-weight: 600;
                text-align: center;
            }
            .no-features-text {
                margin-bottom: $sp-2;
            }
        }

        .feature-search {
            margin-top: $sp-3;
        }

        .features-list {
            min-height: 200px;
            display: flex;
            flex-grow: 1;
            flex-direction: column;
            overflow: auto;

            .feature-list--empty-text {
                color: #555;
                font-size: 14px;
                font-style: italic;
                text-align: center;
            }

            ::v-deep .header {
                padding-top: $sp-3;
                padding-bottom: 0;
            }

            ::v-deep .body {
                padding-bottom: 0;
            }

            .feature--item {
                width: 100%;
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin: $sp-3 0 0;

                .feature-option--checkbox {
                    font-size: 14px;
                    font-weight: normal;
                    color: rgb(30, 35, 43);

                }

                .feature-options--label {
                    color: rgb(145, 150, 157);
                    font-size: 14px;
                    font-weight: normal;
                    text-align: center;

                    span:nth-child(2) {
                        margin-left: $sp-3;
                    }
                }
            }
        }
    }
</style>
<style scoped lang="scss">
    .feature--item {
        ::v-deep .ui-checkbox--color-accent.is-checked {
            .ui-checkbox__checkmark::before {
                /** There are some edge cases that i wasn't able to cover, e.g.
                  * 1. toggle in
                  * 2. waiting for icon to change color
                  * 3. unhover
                  * 4. it has pink color -_-
                  */
                background-color: rgb(64, 159, 236) !important;
                border-color: rgb(64, 159, 236) !important;
            }
            &:active, &:focus, &:hover {
                .ui-checkbox__checkmark::before {
                    background-color: rgb(64, 159, 236);
                    border-color: rgb(64, 159, 236);
                }
            }
        }
    }
</style>
